import { Flex } from "antd"
import React from "react"

import { type CalendarFilterParticipant } from "../../../redux/calendar.types.ts"
import { type Event } from "../../../types/types.ts"
import { CalendarMiniMonth } from "./CalendarMiniMonth.tsx"
import { CalendarMySchedule } from "./CalendarMySchedule.tsx"

interface CalendarSidebarProps {
  events: Event[]
  timezone: string
  userId?: string
  currentDate: Date
  // The Calendar's own Team filter, passed straight through to the
  // mini-month so its dots reflect the same scope the user is viewing —
  // see CalendarMiniMonth/useMiniMonthEventDays for why.
  filterParticipants?: CalendarFilterParticipant[] | null
  onSelectDate: (date: Date) => void
  onSelectEvent: (id: string) => void
  onViewSchedule: () => void
}

// The Calendar 2.0 workspace sidebar — Mini Month Navigator and My Schedule
// today. Team Availability is a deliberately separate, later phase (needs its
// own team-roster fetch this page doesn't do yet); this shell holds whatever
// cards exist without reserving space or stubbing out what isn't built.
export function CalendarSidebar({
  events,
  timezone,
  userId,
  currentDate,
  filterParticipants,
  onSelectDate,
  onSelectEvent,
  onViewSchedule
}: CalendarSidebarProps) {
  // The mini-month's own dots-fetch is cached per month+filter (see
  // useMiniMonthEventDays) so revisiting a month you're already browsing
  // stays instant — but that cache has no way of knowing an event was
  // created/edited/deleted elsewhere on the page unless something tells it
  // to. `events` is already kept fresh by the main calendar's own refetch
  // after every write, so a cheap signature of it (not the array reference,
  // which the CalendarPage.tsx write thunks bump exactly to trigger this)
  // doubles as that signal without the mini-month needing to know anything
  // about *why* it changed.
  const eventsSignature = events
    .map((e) => `${e.id}:${e.start_at}:${e.end_at}`)
    .join("|")

  return (
    <Flex vertical gap={16} className={"w-[300px] shrink-0 overflow-y-auto"}>
      <CalendarMiniMonth
        currentDate={currentDate}
        timezone={timezone}
        filterParticipants={filterParticipants}
        refreshSignal={eventsSignature}
        onSelectDate={onSelectDate}
      />
      <CalendarMySchedule
        events={events}
        timezone={timezone}
        userId={userId}
        onSelectEvent={onSelectEvent}
        onViewAll={onViewSchedule}
      />
    </Flex>
  )
}
